'use client';

import React, { Children, useRef } from 'react';
import clx from 'classnames';

import { generateRandomString } from '../../../helpers/text/textFormatting';
import useGetCardWidth from './hooks/useGetCardWidth';
import useOnReelScroll from './hooks/useOnReelScroll/useOnReelScroll';
import useReelScrollTo from './hooks/useReelScrollTo';
import useIsReelOverflowActive from './hooks/useIsReelOverflowActive';
import { reelVisibleAdjacentCardOffset } from './config/config';

import styles from './Reel.module.css';

interface Props {
    className?: string;
    reelClassName?: string;
    numberToDisplayOnMobile?: 1 | 2;
    gapSize?: 8 | 12 | 16, // In "px".
    children: React.ReactNode;
}

const Reel = ({ className, reelClassName, numberToDisplayOnMobile = 1, gapSize = 16, children }: Props) => {
    const reelRef = useRef<HTMLUListElement | null>(null);
    const indicatorsRef = useRef<HTMLDivElement | null>(null);

    // Removes null and undefined when running toArray().
    const childrenToRender = Children.toArray(children);
    const numberOfChildren = childrenToRender.length;
    const indicators = numberOfChildren
        ? Array(numberOfChildren).fill(null).map(() => generateRandomString())
        : [];

    const isOverflowActive = useIsReelOverflowActive(reelRef);

    const disabled = numberOfChildren <= numberToDisplayOnMobile;

    const cardWidth = useGetCardWidth(reelRef);
    const initialIndicatorWidths = useOnReelScroll(reelRef, indicatorsRef, gapSize, disabled || !isOverflowActive);
    const scrollToItem = useReelScrollTo(reelRef, gapSize);

    // Can't use isCssAnimationTimelineSupported() here because for some reason, nextJS is not hydrating
    // properly and can't render this style attribute dynamically. I suspect it's becaus the server and client
    // values wont match.
    const style = {
        '--gapSize': `${gapSize}px`,
        '--reelVisibleAdjacentCardOffset': `${reelVisibleAdjacentCardOffset * 2}px`,
    }

    let lastItemSpacer = 0;

    // If there are more children than the number of cards specified to display.
    if (
        cardWidth &&
        typeof window !== 'undefined' &&
        numberOfChildren > numberToDisplayOnMobile &&
        isOverflowActive
    ) {
        // Calculate the padding needed at the end to centre the last card in the middle of the screen when
        // the user scrolls to the end.
        // This padding is rendered as an empty <li /> as padding affects the calculations in useOnReelScroll.
        lastItemSpacer = Math.ceil(((window.innerWidth - cardWidth) / 2) - gapSize);
    }

    const classNames = clx(
        className,
        styles.container,
        disabled ? styles.disabled : '',
    );

    const reelClassNames = !disabled ? clx(
        reelClassName,
        styles.track,
        numberToDisplayOnMobile === 1 && numberOfChildren > 1 ? styles.oneOnMobile : '',
        numberToDisplayOnMobile === 2 && numberOfChildren > 2 ? styles.twoOnMobile : '',
        lastItemSpacer === 0 ? styles.padLastItem : '',
    ) : undefined;

    return (
        // @ts-expect-error
        <div className={classNames} style={style}>
            <ul ref={reelRef} className={reelClassNames}>
                {childrenToRender.map((child, index) => (
                    <li key={indicators[index]} className={numberOfChildren === 1 ? 'w-full' : undefined}>
                        {child}
                    </li>
                ))}
                {lastItemSpacer ? (
                    <li aria-hidden className="md:hidden" style={{ minWidth: lastItemSpacer, flex: 'none' }}  />
                ) : null}
            </ul>
            {isOverflowActive && indicators.length > 1 && !disabled ? (
                <div ref={indicatorsRef} className={styles.indicators} aria-hidden="true">
                    {indicators.length ? indicators.map((key, index) => {
                        const handleClick = () => scrollToItem(index);

                        const style = {
                            width: initialIndicatorWidths[index],
                        };

                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={handleClick}
                                className={styles.indicator}
                                title={`View item ${index + 1}`}
                                style={style}
                            />
                        );
                    }) : null}
                </div>
            ) : null}
        </div>
    );
};

export default Reel;
